import { useState } from 'react';
import { apiDownload } from '../../lib/api';

/**
 * Header CSV/PDF export controls (SIL-253 T17, spec §4/§7 Wave 4) for the
 * three financials report screens (book-vs-fmv, depreciation, opportunity)
 * and the scenario comparison view.
 *
 * CSV wires to the T11 (SIL-247) export routes — same apiFetch-to-blob
 * pattern as the SIL-192 ESG CSV export (routes/assets/index.tsx),
 * centralized here as `apiDownload`.
 *
 * PDF (T12, SIL-248) is deferred to Wave 5 — no endpoint exists yet, so the
 * button renders disabled with a tooltip rather than wiring to a route that
 * doesn't exist.
 */
export function ExportCsvButton({ path, filename }: { path: string; filename: string }) {
  const [exporting, setExporting] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    setFailed(false);
    try {
      await apiDownload(path, filename);
    } catch (err) {
      console.error('[financials-export] CSV download failed:', err);
      setFailed(true);
    } finally {
      setExporting(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void handleExport()}
      disabled={exporting}
      className="cursor-pointer rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {exporting ? 'Exporting…' : failed ? 'Export failed — retry' : 'Export CSV'}
    </button>
  );
}

export function ExportPdfComingSoonButton() {
  return (
    <button
      type="button"
      disabled
      title="PDF export coming soon"
      className="cursor-not-allowed rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-400"
    >
      Export PDF
    </button>
  );
}

export function ExportButtonGroup({ csvPath, csvFilename }: { csvPath: string; csvFilename: string }) {
  return (
    <div className="flex items-center gap-2">
      <ExportCsvButton path={csvPath} filename={csvFilename} />
      <ExportPdfComingSoonButton />
    </div>
  );
}
